import { CHAT_SEARCH_MIN_QUERY_LENGTH } from "../constants/chat";
import type { ICrossChatSearchHit } from "../types/chat";

export interface IResolveSemanticSearchHitsProps {
  // NOTE: The raw response. `undefined` while the query has not resolved yet,
  // which reads the same as "no hits" here — the caller tells the two apart
  // through its own loading flag.
  hits: readonly ICrossChatSearchHit[] | undefined;
  // NOTE: Chats the keyword path already returned — the pages loaded so far —
  // so the same chat is not offered twice under two headings.
  keywordChatUuids: readonly string[];
  // NOTE: The needle the semantic request was actually made with.
  debouncedQuery: string;
  // NOTE: Derived from the LIVE input, not the debounced one, so deleting a
  // query back below the minimum drops the section on the keystroke rather
  // than a debounce window later.
  isQueryBelowMinLength: boolean;
}

// NOTE: NSAI-4336 — the cross-chat semantic section's gating rule, shared by
// web's SearchChats and mobile's SearchChatsModal so the two cannot drift on
// what counts as "nothing to show".
//
// Tri-state on purpose. `null` means "no semantic search applies to this box"
// — an empty query (browse mode) or one still below the minimum — and lets the
// caller drop the whole section rather than render an empty state under the
// keyword results. An empty ARRAY means the API answered with zero hits, which
// is a real answer.
//
// Deduping keeps the keyword row and drops the semantic one: both navigate to
// the same chat, and the keyword match is the more precise of the two.
export const resolveSemanticSearchHits = ({
  hits,
  keywordChatUuids,
  debouncedQuery,
  isQueryBelowMinLength,
}: IResolveSemanticSearchHitsProps): ICrossChatSearchHit[] | null => {
  if (isQueryBelowMinLength) {
    return null;
  }

  if (debouncedQuery.trim().length < CHAT_SEARCH_MIN_QUERY_LENGTH) {
    return null;
  }

  // NOTE: One entry per chat. `seen` starts as the keyword matches and then
  // absorbs each semantic hit it keeps, so the filter does two jobs at once:
  // it drops a chat the keyword section already lists, and it collapses two
  // semantic hits that point at the same chat. The second case is real — the
  // server searches per MESSAGE and does not collapse per chat, so two
  // messages in one conversation can both clear the score threshold. Both rows
  // would navigate to the same chat, so showing them twice reads as a bug.
  // Hits arrive in descending score order, so the one kept is the best match.
  const seenChatUuids = new Set(keywordChatUuids);
  return (hits ?? []).filter((hit) => {
    if (seenChatUuids.has(hit.chatUuid)) {
      return false;
    }
    seenChatUuids.add(hit.chatUuid);
    return true;
  });
};

export interface IHasSemanticSectionProps {
  hits: readonly ICrossChatSearchHit[] | null;
  isLoading: boolean;
  isError: boolean;
}

// NOTE: Whether the semantic section is worth mounting at all. A zero-hit
// response next to a populated keyword list would just be a second empty state
// stacked under the first, so the section disappears instead; loading and error
// still show, because both are states the user can act on (wait, or retry).
export const hasSemanticSection = ({
  hits,
  isLoading,
  isError,
}: IHasSemanticSectionProps): boolean =>
  hits !== null && (isLoading || isError || hits.length > 0);
